import React, {
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { PANEL_TRANSITION_TIMEOUT } from '@/config/constants';
import { Service } from '@/graphql';
import { Fade, Slide } from '@material-ui/core';
import { pathOr } from 'ramda';

import { ILayoutProps } from '../Layout';
import { AsaphDialogHeader, AsaphDialogHeaderButton } from '../shared/Dialog';
import { Chip } from '../shared/Chip';
import { useAuth } from '@/providers/AuthProvider';
import { useTeam } from '@/providers/TeamProvider';
import AsaphDialog from '../shared/Dialog/AsaphDialog';
import styles from './AccountPanel.module.scss';
import AccountRoot from './components/AccountRoot';
import { ChurchProfile } from './components/ChurchProfile';
import { DeleteAccount } from './components/DeleteAccount';
import { DeleteAccountVerify } from './components/DeleteAccountVerify';
import { Services } from './components/Services';
import { CreateUpdateService } from './components/Services/CreateUpdateService';
import { SettingAccount } from './components/SettingAccount';
// import { SettingEmailNotifications } from './SettingEmailNotifications';
// import { SettingProgress } from './SettingProgress';
import { SettingAddTeamMember } from './components/SettingAddTeamMember';
import { SettingChartRepository } from './components/SettingChartRepository';
import { SettingChurchProfile } from './components/SettingChurchProfile';
// import { SettingActiveStatus } from './SettingActiveStatus';
import { SettingDarkMode } from './components/SettingDarkMode';
import { SettingEditProfile } from './components/SettingEditProfile';
import { SettingEditTeamName } from './components/SettingEditTeamName';
import { SettingPushNotifications } from './components/SettingPushNotifications';
import { SettingServing } from './components/SettingServing';
import { SettingTeam } from './components/SettingTeam';
import { SettingUpgrade } from './components/SettingUpgrade';
import { UsageAndLimits } from './components/UsageAndLimits';

export enum EAccountDisplayState {
	ROOT,
	PROGRESS,
	EDIT_PROFILE,
	SERVING,
	DARK_MODE,
	ACTIVE_STATUS,
	PUSH_NOTIFICATIONS,
	EMAIL_NOTIFICATIONS,
	CHURCH_PROFILE,
	TEAM,
	ADD_TEAM_MEMBER,
	EDIT_TEAM_NAME,
	SERVICES,
	CREATE_UPDATE_SERVICE,
	ACCOUNT_SETTINGS,
	USAGE_AND_LIMITS,
	UPGRADE,
	BILLING,
	DELETE_ACCOUNT,
	DELETE_ACCOUNT_VERIFICATION,
	CHART_STORAGE,
}

interface AccountPanelProps {
	open: boolean;
	setOpen: ILayoutProps['setAccountOpen'];
	defaultDisplayState?: EAccountDisplayState;
}

const displayStateComponents: Map<
	EAccountDisplayState,
	{
		title?: string;
		component: React.FC<AccountDisplayProps>;
	}
> = new Map([
	[EAccountDisplayState.ROOT, { component: AccountRoot }],
	// [
	// 	EAccountDisplayState.PROGRESS,
	// 	{
	// 		title: user.displayName ?? 'Progress',
	// 		component: SettingProgress,
	// 	},
	// ],
	[
		EAccountDisplayState.EDIT_PROFILE,
		{
			title: 'Edit profile',
			component: SettingEditProfile,
		},
	],
	[
		EAccountDisplayState.SERVING,
		{
			title: 'Serving',
			component: SettingServing,
		},
	],
	[
		EAccountDisplayState.DARK_MODE,
		{ title: 'Dark mode', component: SettingDarkMode },
	],
	// [
	// 	EAccountDisplayState.ACTIVE_STATUS,
	// 	{ title: 'Active status', component: SettingActiveStatus },
	// ],
	// [
	// 	EAccountDisplayState.EMAIL_NOTIFICATIONS,
	// 	{
	// 		title: 'Email notifications',
	// 		component: SettingEmailNotifications,
	// 	},
	// ],
	[
		EAccountDisplayState.PUSH_NOTIFICATIONS,
		{
			title: 'Notifications',
			component: SettingPushNotifications,
		},
	],
	[
		EAccountDisplayState.TEAM,
		{ title: 'Team members', component: SettingTeam },
	],
	[
		EAccountDisplayState.ADD_TEAM_MEMBER,
		{
			component: SettingAddTeamMember,
		},
	],
	[
		EAccountDisplayState.CHURCH_PROFILE,
		{
			title: 'Church profile',
			component: SettingChurchProfile,
		},
	],
	[
		EAccountDisplayState.EDIT_TEAM_NAME,
		{
			title: 'Team Name',
			component: SettingEditTeamName,
		},
	],
	[
		EAccountDisplayState.SERVICES,
		{
			title: 'Services',
			component: Services,
		},
	],
	[
		EAccountDisplayState.CREATE_UPDATE_SERVICE,
		{
			title: 'Create Service',
			component: CreateUpdateService,
		},
	],
	[
		EAccountDisplayState.ACCOUNT_SETTINGS,
		{
			title: 'Account settings',
			component: SettingAccount,
		},
	],
	[
		EAccountDisplayState.USAGE_AND_LIMITS,
		{
			title: 'Plan',
			component: UsageAndLimits,
		},
	],
	[
		EAccountDisplayState.DELETE_ACCOUNT,
		{
			title: 'Delete account',
			component: DeleteAccount,
		},
	],
	[
		EAccountDisplayState.DELETE_ACCOUNT_VERIFICATION,
		{
			title: 'Delete account verification',
			component: DeleteAccountVerify,
		},
	],
	[
		EAccountDisplayState.CHART_STORAGE,
		{
			title: 'Chart storage',
			component: SettingChartRepository,
		},
	],
]);
const displayStateKeys = Array.from(displayStateComponents.keys());
const defaultDisplayStateStack = [EAccountDisplayState.ROOT];

export interface AccountDisplayProps {
	pushDisplayState: (displayState: EAccountDisplayState) => void;
	popDisplayState: () => void;
	setOpen: ILayoutProps['setAccountOpen'];
	panelTransitionTimeout: number;
	setCustomTitle: React.Dispatch<React.SetStateAction<string | undefined>>;
	customTitle?: string;
	setService: React.Dispatch<React.SetStateAction<Partial<Service>>>;
	service: Partial<Service>;
}

const AccountPanel: React.FC<AccountPanelProps> = ({
	open,
	setOpen,
	defaultDisplayState,
}) => {
	const { user } = useAuth();
	const { team } = useTeam();
	const isCurrentUserAdmin =
		team?.members.find((m) => m.user.id === user?.id)?.roles.includes('teamAdmin') ?? false;
	const [displayStateStack, setDisplayStateStack] = useState(
		defaultDisplayState
			? [...defaultDisplayStateStack, defaultDisplayState]
			: defaultDisplayStateStack,
	);
	const [displayState, setDisplayState] = useState<EAccountDisplayState>(
		getDisplayState(displayStateStack),
	);
	const [customTitle, setCustomTitle] = useState<string | undefined>(
		undefined,
	);
	const [service, setService] = useState<Partial<Service>>({});

	const panelTitle = customTitle || getNameForDisplayState(displayState);

	useEffect(() => {
		setDisplayState(getDisplayState(displayStateStack));
	}, [displayStateStack]);

	const pushDisplayState = useCallback(
		(newState: EAccountDisplayState) => {
			setDisplayStateStack([...displayStateStack, newState]);
		},
		[displayStateStack],
	);

	const popDisplayState = useCallback(() => {
		if (displayStateStack.length > 1) {
			const newStack = [...displayStateStack];
			newStack.pop();
			setDisplayStateStack(newStack);
			setCustomTitle(undefined);
		}
	}, [displayStateStack]);

	useEffect(() => {
		if (open) {
			setDisplayStateStack(
				defaultDisplayState
					? [...defaultDisplayStateStack, defaultDisplayState]
					: defaultDisplayStateStack,
			);
		} else {
			const timer = setTimeout(() => {
				setDisplayStateStack(defaultDisplayStateStack);
				setCustomTitle(undefined);
				setService({});
			}, PANEL_TRANSITION_TIMEOUT);
			return () => clearTimeout(timer);
		}
	}, [open, defaultDisplayState]);

	const handleClose = useCallback(() => {
		setOpen({
			open: false,
		});
	}, [setOpen]);

	const renderHeaderStart = useCallback((): ReactNode => {
		switch (displayState) {
			case EAccountDisplayState.ROOT:
				return (
					<AsaphDialogHeaderButton
						type="text"
						label="Done"
						handlePress={handleClose}
					/>
				);
			case EAccountDisplayState.EDIT_PROFILE:
			case EAccountDisplayState.SERVING:
			case EAccountDisplayState.ADD_TEAM_MEMBER:
			case EAccountDisplayState.EDIT_TEAM_NAME:
				case EAccountDisplayState.CREATE_UPDATE_SERVICE:
			case EAccountDisplayState.PROGRESS:
			case EAccountDisplayState.ACCOUNT_SETTINGS:
			case EAccountDisplayState.USAGE_AND_LIMITS:
			case EAccountDisplayState.ACTIVE_STATUS:
			case EAccountDisplayState.DARK_MODE:
			case EAccountDisplayState.EMAIL_NOTIFICATIONS:
			case EAccountDisplayState.PUSH_NOTIFICATIONS:
			case EAccountDisplayState.TEAM:
			case EAccountDisplayState.SERVICES:
			case EAccountDisplayState.DELETE_ACCOUNT:
			case EAccountDisplayState.DELETE_ACCOUNT_VERIFICATION:
			case EAccountDisplayState.CHURCH_PROFILE:
			case EAccountDisplayState.CHART_STORAGE:
				return (
					<AsaphDialogHeaderButton
						type="text"
						label="Back"
						handlePress={() => {
							popDisplayState();
							setService({});
						}}
					/>
				);
			default:
				return null;
		}
	}, [displayState, popDisplayState]);

	const renderHeaderEnd = useCallback((): ReactNode => {
		if (displayState === EAccountDisplayState.ROOT && isCurrentUserAdmin) {
			return <Chip variant="rounded" color="light-gray">Admin</Chip>;
		}
		return null;
	}, [displayState, isCurrentUserAdmin]);

	return (
		<AsaphDialog open={open} onClose={handleClose}>
			<div className={styles['account-panel__toolbar']}>
				<AsaphDialogHeader
					leadingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: renderHeaderStart(),
					}}
					title={{
						needsFadeIn: !!panelTitle,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						label: panelTitle,
					}}
					trailingButton={{
						needsFadeIn: displayState === EAccountDisplayState.ROOT && isCurrentUserAdmin,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: renderHeaderEnd(),
					}}
				/>
			</div>
			{displayState !== EAccountDisplayState.ROOT && (
				<div className={styles['account-panel__toolbar-divider']}>
					<hr />
				</div>
			)}
			<section className={styles['account-panel__content']}>
				{displayStateKeys.map((key) => {
					const displayStateComponent =
						displayStateComponents.get(key);
					if (!displayStateComponent) {
						return null;
					}
					const Component = displayStateComponent.component;

					if (key === EAccountDisplayState.ROOT) {
						return (
							<Fade
								key={key}
								in={displayState === key}
								timeout={PANEL_TRANSITION_TIMEOUT}
								mountOnEnter
								unmountOnExit
							>
								<div
									className={
										styles[
											'account-panel__content-container'
										]
									}
								>
									<Component
										pushDisplayState={pushDisplayState}
										popDisplayState={popDisplayState}
										setOpen={setOpen}
										panelTransitionTimeout={
											PANEL_TRANSITION_TIMEOUT
										}
										setCustomTitle={setCustomTitle}
										customTitle={customTitle}
										setService={setService}
										service={service}
									/>
								</div>
							</Fade>
						);
					}

					return (
						<Slide
							key={key}
							direction="left"
							in={
								displayState === key ||
								displayStateStack.indexOf(key) >= 0
							}
							timeout={PANEL_TRANSITION_TIMEOUT}
							mountOnEnter
							unmountOnExit
						>
							<div
								className={
									styles['account-panel__content-container']
								}
								// Needed to prevent underlying Components' absolute positioned elements be on top of new Component
								style={{
									zIndex: displayStateStack.indexOf(key) + 1,
								}}
							>
								<Component
									pushDisplayState={pushDisplayState}
									popDisplayState={popDisplayState}
									setOpen={setOpen}
									panelTransitionTimeout={
										PANEL_TRANSITION_TIMEOUT
									}
									setCustomTitle={setCustomTitle}
									customTitle={customTitle}
									setService={setService}
									service={service}
								/>
							</div>
						</Slide>
					);
				})}
			</section>
		</AsaphDialog>
	);
};

export default AccountPanel;

function getDisplayState(displayStateStack: EAccountDisplayState[]) {
	return displayStateStack.length
		? displayStateStack[displayStateStack.length - 1]
		: EAccountDisplayState.ROOT;
}

const getNameForDisplayState = (displayState: EAccountDisplayState): string => {
	const displayStateComponent = displayStateComponents.get(displayState);
	if (displayStateComponent) {
		return pathOr('', ['title'], displayStateComponent);
	}
	return '';
};
